import { ConfigType } from '@shared/config/types';
import { UtiqStatusEntryName } from '@shared/constants/utiq';
import { getValidatedBase64Cookie } from '@shared/cookies/getValidatedBase64Cookie';
import { readCookie } from '@shared/cookies/readCookie';
import { ReadCookieFunction } from '@shared/cookies/types';
import { consoleLogger } from '@shared/logging/consoleLogger';
import { processConsentChangeFunction } from '@utiqLoader/consentHandling/getProcessConsentChangeFunction';
import { OptanonData } from '@utiqLoader/consentManagementProvidersHandlers/constants';
import { areVendorsEnabled } from '@utiqLoader/consentManagementProvidersHandlers/providers/optanon/consent/areVendorsEnabled';
import { handleDataClear } from '@utiqLoader/helpers/handleDataClear';
import { addEventListener } from '@shared/api/sharedAPIEventFunctions';
import { setPollingOnce } from '@shared/helpers/setPolling';
import { getHostname } from '@shared/getUtiqHost';
import { utiqMachine } from '@machine/index';
import { callEventListeners } from '@shared/events/callEventListeners';

const getGroupsWithConsentFromCookie = ({
  cookie,
  purposeId,
  vendorId,
}: {
  cookie: string;
  purposeId?: string;
  vendorId?: string;
}) => {
  /* Function for purpose consent handling */
  const getPurposeConsentStatus = () => {
    const optanonCookieGroups = new URLSearchParams(cookie).get(
      OptanonData.groupsString,
    );
    if (optanonCookieGroups?.includes(`${purposeId}:1`)) {
      return true;
    } else if (optanonCookieGroups?.includes(`${purposeId}:0`)) {
      return false;
    }

    return null;
  };

  /* Function for vendor consent handling */
  const getVendorConsentStatus = () => {
    /* If vendors are disabled, no vendor explicit consent is required */
    if (!areVendorsEnabled()) {
      return true;
    }
    const optanonCookieVendors = new URLSearchParams(cookie).get(
      OptanonData.vendorsString,
    );
    if (optanonCookieVendors?.includes(`${vendorId}:1`)) {
      return true;
    } else if (optanonCookieVendors?.includes(`${vendorId}:0`)) {
      return false;
    }

    return null;
  };

  /* Return true/false or null if the decision seems not to be made yet (no value in the cookie) */
  const purposeConsentStatus = getPurposeConsentStatus();
  const vendorConsentStatus = getVendorConsentStatus();

  const isConsentGranted: boolean | null =
    purposeConsentStatus === null || vendorConsentStatus === null
      ? null
      : purposeConsentStatus && vendorConsentStatus;

  return { isConsentGranted };
};

const isOptanonConsentGranted = ({
  readCookie,
  config,
}: {
  readCookie: ReadCookieFunction;
  config: ConfigType;
}) => {
  const cookie = readCookie(document.cookie, OptanonData.cookieName);
  const { isConsentGranted } = cookie
    ? getGroupsWithConsentFromCookie({
        cookie,
        purposeId: config.cmpUtiqPurposeId,
        vendorId: config.cmpUtiqVendorId,
      })
    : { isConsentGranted: null };
  return isConsentGranted;
};

const checkConsentGranted = (Utiq: UtiqEnvironment, cookie: string) =>
  cookie
    ? getGroupsWithConsentFromCookie({
        cookie,
        purposeId: Utiq?.config?.cmpUtiqPurposeId,
        vendorId: Utiq?.config?.cmpUtiqVendorId,
      }).isConsentGranted
    : null;

export async function setupCMPOptanonBeforeUtiq(): Promise<void> {
  const Utiq = window.Utiq;
  if (!Utiq) throw new Error('window.Utiq === undefined');

  return new Promise((done) => {
    const setOptanonConsentSynchronizationCallbacks = () => {
      let cookie = readCookie(document.cookie, OptanonData.cookieName);
      const isConsentGranted: boolean | null = checkConsentGranted(
        Utiq,
        cookie,
      );

      if (isConsentGranted === true) {
        return done();
      } else {
        if (
          isConsentGranted === false &&
          window.Optanon?.IsAlertBoxClosed() === true
        ) {
          handleDataClear();
          return done();
        }
        let isFirstCall = true;
        const consentChangeCallback = () => {
          if (isFirstCall) {
            cookie = readCookie(document.cookie, OptanonData.cookieName);
            const isConsentGranted: boolean | null = checkConsentGranted(
              Utiq,
              cookie,
            );

            const consentStatusCookie = getValidatedBase64Cookie(
              document.cookie,
              UtiqStatusEntryName,
            );

            const utiqConsentAccepted = consentStatusCookie === 'true';
            const utiqConsentRejected = consentStatusCookie === 'false';
            const utiqConsentExists =
              utiqConsentAccepted || utiqConsentRejected;

            if (
              !utiqConsentExists ||
              utiqConsentAccepted !== isConsentGranted
            ) {
              if (isConsentGranted === false) {
                handleDataClear();
                return done();
              }
              if (typeof isConsentGranted === 'boolean') {
                return done();
              }
            }

            if (utiqConsentAccepted === isConsentGranted) {
              utiqMachine.run();
              callEventListeners('onFlowCompleted', 'Utiq', {});
            }

            isFirstCall = false;
          }
        };
        window.Optanon?.OnConsentChanged(consentChangeCallback);
      }
    };

    setPollingOnce(
      () => {
        // even when window.Optanon?.UpdateConsent === 'function' is true, optanon still has some internal state that causes this process to fail
        // we add an aditional time out to give Optanon time to sort themselves out
        setTimeout(setOptanonConsentSynchronizationCallbacks, 250);
      },
      () =>
        typeof window.Optanon?.IsAlertBoxClosed === 'function' &&
        typeof window.Optanon?.OnConsentChanged === 'function',
    );
  });
}

export async function setupCMPOptanonAfterUtiq() {
  const Utiq = window.Utiq;
  if (!Utiq) throw new Error('window.Utiq === undefined');

  const config = Utiq.config;
  if (!config) return;

  const processConsent = () => {
    const cookie = readCookie(document.cookie, OptanonData.cookieName);
    consoleLogger.logInfo('Processing OptanonConsent cookie with value: ', {
      cookie,
    });
    const { isConsentGranted } = cookie
      ? getGroupsWithConsentFromCookie({
          cookie,
          purposeId: config.cmpUtiqPurposeId,
          vendorId: config.cmpUtiqVendorId,
        })
      : { isConsentGranted: false };

    const hostname = getHostname();

    processConsentChangeFunction(Utiq, !!isConsentGranted, {
      [hostname]: {
        name: 'optanonConsent',
        value: `optanonConsent=${isConsentGranted}`,
        consentParsed: isConsentGranted
          ? `${config.cmpUtiqPurposeId}:1`
          : `${config.cmpUtiqPurposeId}:0`,
        consentState: isConsentGranted ? 'accepted' : 'rejected',
      },
    });
  };

  const runOneTrustUtiqFlow = () => {
    const utiqConsentGranted = getValidatedBase64Cookie(
      document.cookie,
      UtiqStatusEntryName,
    );
    const optanonConsentGranted = isOptanonConsentGranted({
      readCookie,
      config,
    });
    if (utiqConsentGranted === 'false' && optanonConsentGranted === true) {
      if (window.Optanon?.UpdateConsent) {
        window.Optanon.UpdateConsent(
          'Category',
          `${config.cmpUtiqPurposeId}:0`,
        );
        if (areVendorsEnabled()) {
          window.Optanon.UpdateConsent(
            'General Vendor',
            `${config.cmpUtiqVendorId}:0`,
          );
        }
      }
    } else {
      processConsent();
    }

    const consentChangeCallback = () => {
      consoleLogger.logDebug(`Optanon.OnConsentChanged was dispatched`);
      const optanonConsentGranted = isOptanonConsentGranted({
        readCookie,
        config,
      });
      const utiqConsentGranted =
        getValidatedBase64Cookie(document.cookie, UtiqStatusEntryName) ===
        'true';

      consoleLogger.logDebug(
        'Current Utiq consent status:',
        utiqConsentGranted,
      );
      consoleLogger.logDebug(
        'Current Optanon consent status:',
        optanonConsentGranted,
      );
      consoleLogger.logDebug(
        'Consent statuses updated, processing the consent for synchronization...',
      );
      window.Utiq ||= {};
      window.Utiq.syncVersionCookies = true;
      processConsent();
    };
    window.Optanon?.OnConsentChanged(consentChangeCallback);

    /*
     * Synchronize the Optanon consent when the update is finished -
     * covers data clear and 204 data call consent rejection from ConsentHub
     */
    addEventListener('onConsentChanging', (evt: unknown) => {
      if (
        !(
          typeof evt === 'object' &&
          evt !== null &&
          'isConsentGranted' in evt &&
          typeof evt.isConsentGranted === 'boolean'
        )
      )
        return;

      const { isConsentGranted } = evt;

      consoleLogger.logDebug(
        'Synchronizing Optanon consent with consent value:',
        isConsentGranted,
      );
      if (window.Optanon?.UpdateConsent) {
        if (isConsentGranted) {
          window.Optanon.UpdateConsent(
            'Category',
            `${config.cmpUtiqPurposeId}:1`,
          );
          if (areVendorsEnabled()) {
            window.Optanon.UpdateConsent(
              'General Vendor',
              `${config.cmpUtiqVendorId}:1`,
            );
          }
        } else {
          window.Optanon.UpdateConsent(
            'Category',
            `${config.cmpUtiqPurposeId}:0`,
          );
          if (areVendorsEnabled()) {
            window.Optanon.UpdateConsent(
              'General Vendor',
              `${config.cmpUtiqVendorId}:0`,
            );
          }
        }
      }
      consoleLogger.logDebug(
        'Processing consent from onConsentChanging event with consent value:',
        isConsentGranted,
      );
    });

    /*
     * Synchronize the Optanon consent when the update is finished
     * - covers data clear and 204 data call consent rejection from
     * ConsentHub
     */
    addEventListener('onConsentUpdateFinished', (evt: unknown) => {
      if (
        !(
          typeof evt === 'object' &&
          evt !== null &&
          'isConsentGranted' in evt &&
          typeof evt.isConsentGranted === 'boolean'
        )
      )
        return;

      const { isConsentGranted } = evt;
      consoleLogger.logDebug(
        'Synchronizing Optanon consent with consent value:',
        isConsentGranted,
      );
      const cookie = readCookie(document.cookie, OptanonData.cookieName);
      const { isConsentGranted: optanonConsentGranted } = cookie
        ? getGroupsWithConsentFromCookie({
            cookie,
            purposeId: config.cmpUtiqPurposeId,
            vendorId: config.cmpUtiqVendorId,
          })
        : { isConsentGranted: null };
      if (window.Optanon?.UpdateConsent) {
        if (isConsentGranted === false && optanonConsentGranted === true) {
          window.Optanon?.UpdateConsent(
            'Category',
            `${config.cmpUtiqPurposeId}:0`,
          );
          if (areVendorsEnabled()) {
            window.Optanon?.UpdateConsent(
              'General Vendor',
              `${config.cmpUtiqVendorId}:0`,
            );
          }
        } else if (
          isConsentGranted === true &&
          optanonConsentGranted === false
        ) {
          window.Optanon?.UpdateConsent(
            'Category',
            `${config.cmpUtiqPurposeId}:1`,
          );

          if (areVendorsEnabled()) {
            window.Optanon?.UpdateConsent(
              'General Vendor',
              `${config.cmpUtiqVendorId}:1`,
            );
          }
        } else if (
          isConsentGranted === undefined &&
          optanonConsentGranted !== undefined
        ) {
          if (typeof window.Utiq?.API?.handleConsentChange === 'function') {
            window.Utiq.API.handleConsentChange(optanonConsentGranted === true);
          }
        }
      }
    });
  };

  setPollingOnce(
    () => {
      // even when window.Optanon?.UpdateConsent === 'function' is true, optanon still has some internal state that causes this process to fail
      // we add an aditional time out to give Optanon time to sort themselves out
      setTimeout(runOneTrustUtiqFlow, 250);
    },
    () =>
      typeof window.Optanon?.UpdateConsent === 'function' &&
      typeof window.Optanon?.OnConsentChanged === 'function',
  );
}
